<div class="room-login">
  <andor-app-update-notice />
  <andor-trial-notice />
  @let status = odin.status();
  @if (odin.pendingApproval()) {
    <andor-fieldset size="fill" class="content" [direction]="'center'">
      <andor-join-approval-overlay />
    </andor-fieldset>
  } @else if (status === OdinConnectionStatus.connecting) {
    <andor-fieldset size="fill" class="content" [direction]="'center'">
      <div class="loading">
        <mat-spinner></mat-spinner>
        <andor-button (click)="cancel()">
          <span>{{ 'Cancel' | translate }}</span>
        </andor-button>
      </div>
    </andor-fieldset>
  } @else if (odin.connectionError(); as error) {
    <andor-fieldset size="fill" class="content" [direction]="'center'">
      <div class="error-container">
        <img
          class="logo"
          src="/assets/icon_sad_{{ userSettingsStore.colorMode$ | async }}.svg"
          alt="" />
        @if (error.allowRetry) {
          <div class="warning">{{ error.reason }}</div>
        } @else {
          <div class="error">{{ error.reason }}</div>
        }
      </div>
    </andor-fieldset>
    <andor-fieldset>
      <div class="actions">
        @if (error.allowRetry) {
          <andor-button (click)="retry()">
            <span>{{ 'Yes' | translate }}</span>
          </andor-button>
          &nbsp;
          <andor-button (click)="retry(true)">
            <span>{{ 'Yes (Always)' | translate }}</span>
          </andor-button>
          &nbsp;
          <andor-button (click)="cancel()">
            <span>{{ 'No' | translate }}</span>
          </andor-button>
        } @else {
          <andor-button (click)="cancel()">
            <span>{{ 'Back' | translate }}</span>
          </andor-button>
        }
      </div>
    </andor-fieldset>
  } @else if (showFullPlan()) {
    <andor-fieldset>
      <p class="plan-intro">
        {{
          'This is the room plan. Here you can get an overview of all available public rooms.'
            | translate
        }}
      </p>
    </andor-fieldset>

    <andor-fieldset size="fill" class="full-plan" [scroll]="true">
      <andor-room-plan
        [selectedRoomId]="selectedRoomId()"
        (roomSelected)="onFullPlanRoomSelected($event)" />
    </andor-fieldset>

    <andor-fieldset>
      <div class="actions">
        <andor-button (click)="showFullPlan.set(false)">
          <span>{{ 'Back to login' | translate }}</span>
        </andor-button>
      </div>
    </andor-fieldset>
  } @else {
    <andor-fieldset class="plan">
      <p class="intro">{{ 'Join an existing room or start a new conversation.' | translate }}</p>
      <andor-room-plan
        [rooms]="planRooms()"
        [selectedRoomId]="selectedRoomId()"
        (roomSelected)="onPlanRoomSelected($event)" />
      <div class="schedule-row">
        @if (displayedSchedule(); as schedule) {
          <andor-schedule-display [schedule]="schedule" />
        }
        @if (hasMoreRooms()) {
          <andor-button
            density="compact"
            class="all-rooms-btn"
            [class.all-rooms-btn--corner]="displayedSchedule()"
            (click)="openFullPlan()">
            {{ 'See all rooms' | translate }}
          </andor-button>
        }
      </div>
    </andor-fieldset>

    <andor-fieldset size="fill" class="fields" [direction]="'center'">
      <div class="login-area" (andorSubmit)="join()" [andorSubmitDisabled]="isJoinDisabled()">
        <div class="entry room-entry">
          <andor-autocomplete-field
            class="room-field"
            [formControl]="selectedRoomControl"
            [placeholder]="'Search rooms…' | translate"
            [items]="filteredRooms()"
            [displayWith]="displayRoom"
            [itemKey]="roomKey"
            [emptyText]="'No rooms available' | translate"
            [inlineActions]="roomSearchActions()"
            (queryChange)="onRoomQuery($event)"
            (selected)="onRoomPicked($event)">
            <ng-template #option let-room>
              @if (room.roomId === instantMeetingId) {
                <span class="room-option room-option--instant">
                  <fa-duotone-icon [icon]="faBolt"></fa-duotone-icon>
                  <span class="room-option__name">{{ 'New instant meeting' | translate }}</span>
                </span>
              } @else {
                <span class="room-option">
                  <span class="room-option__name" [andorTextTicker]="room.name">
                    <span class="ticker-text">{{ room.name }}</span>
                  </span>
                  <span class="room-option__meta">
                    @if (room.isPrivate) {
                      <fa-icon [icon]="faLock" [andorTooltip]="'Private' | translate"></fa-icon>
                    }
                    @if (room.hasPassword) {
                      <fa-icon
                        [icon]="faKey"
                        [andorTooltip]="'Password protected' | translate"></fa-icon>
                    }
                    @if (room.schedule) {
                      <fa-icon [icon]="faClock" [andorTooltip]="'Scheduled' | translate"></fa-icon>
                    }
                    <span class="room-option__count">
                      {{ room.memberCount }}
                      <fa-icon
                        [icon]="faPeopleGroup"
                        [andorTooltip]="'Online' | translate"></fa-icon>
                    </span>
                  </span>
                </span>
              }
            </ng-template>
          </andor-autocomplete-field>

          <button
            type="button"
            class="create-btn"
            [disabled]="!hasSelectedRoom()"
            [attr.aria-label]="'Copy invite link' | translate"
            [andorTooltip]="
              (hasSelectedRoom() ? 'Copy invite link' : 'Select a room to copy its invite link')
                | translate
            "
            (click)="copyInviteLink()">
            <fa-duotone-icon [icon]="faLink"></fa-duotone-icon>
          </button>

          @if (canManageRooms()) {
            <button
              type="button"
              class="create-btn"
              [disabled]="!hasSelectedRoom()"
              [attr.aria-label]="'Edit room' | translate"
              [andorTooltip]="
                (hasSelectedRoom() ? 'Edit room' : 'Select a room to edit it') | translate
              "
              (click)="openEditDialog()">
              <fa-duotone-icon [icon]="faPenToSquare"></fa-duotone-icon>
            </button>
            <button
              type="button"
              class="create-btn"
              [disabled]="trial.expired()"
              [attr.aria-label]="'Create room' | translate"
              [andorTooltip]="'Create room' | translate"
              (click)="openCreateDialog()">
              <fa-duotone-icon [icon]="faPlus"></fa-duotone-icon>
            </button>
          }
        </div>

        <div class="entry">
          <andor-text-field
            [formControl]="userNameControl"
            [placeholder]="'Desired Display Name' | translate"
            [errorMessages]="{
              required: 'Your name is required.' | translate,
              minlength: 'Your name must be at least 3 characters long.' | translate,
              pattern: 'Your name must start with a letter.' | translate,
            }"></andor-text-field>
        </div>

        @if (passwordRoomId(); as roomId) {
          <div class="entry">
            <andor-e2e-encryption-panel [roomName]="roomId" />
          </div>
        }
      </div>
    </andor-fieldset>

    <andor-fieldset>
      <div class="actions">
        <andor-button
          class="join-btn"
          type="gradient"
          (click)="join()"
          [disabled]="isJoinDisabled()">
          @if (instantMeetingSelected()) {
            <span>{{ 'Create instant meeting' | translate }}</span>
          } @else {
            <span>{{ 'Join Room' | translate }}</span>
          }
        </andor-button>
      </div>
      @if (developerMode()) {
        <a class="dev-settings" (click)="openDevSettings()">{{ 'Developer Mode' | translate }}</a>
      }
    </andor-fieldset>
  }
</div>
